import {createContext, useContext, useEffect, useState, useCallback} from "react";

import {
    signInWithEmailAndPassword,
    signOut,
    createUserWithEmailAndPassword,
    onAuthStateChanged,
    sendEmailVerification} from "firebase/auth"
import {auth, db} from "../firebase";
import {update, ref} from "firebase/database";
import {getAssociationByEmail, getEngineerByEmail} from "../services/associationService";

const userAuthContext = createContext();

// Emails que têm acesso ao dashboard de administração
const ADMIN_EMAILS = ["admin@e4iu.com"];

export function UserAuthContextProvider({children}) {

    const [user, setUser] = useState(null);
    // null = ainda a carregar; 'admin' | 'association' | 'engineer' | 'none'
    const [role, setRole] = useState(null);
    // Registo do utilizador no RTDB (associação ou engenheiro), se existir
    const [profile, setProfile] = useState(null);
    const [roleLoading, setRoleLoading] = useState(true);
    // true até o Firebase terminar de restaurar a sessão (evita redirecionamentos prematuros)
    const [authLoading, setAuthLoading] = useState(true);

    function signUp(email, password){
        return createUserWithEmailAndPassword(auth, email, password);
    }

    /**
     * Envia o email de verificação.
     * @param {object} [user] — utilizador a verificar; por omissão usa o utilizador
     *   atual da sessão. No registo, passa o user retornado pelo signUp para não
     *   depender do estado da sessão (auth.currentUser pode ainda não estar definido).
     */
    function verifyEmail(user){
        const target = user || auth.currentUser;
        if (!target) {
            const err = new Error("Não existe uma sessão ativa para enviar o email de verificação.");
            err.code = "auth/no-current-user";
            throw err;
        }
        return sendEmailVerification(target);
    }

    function logIn(email,password){
        return signInWithEmailAndPassword(auth, email, password);
    }

    function logOut(){
        return signOut(auth);
    }

    /**
     * Quando o email já está verificado no Firebase Auth mas o registo na
     * plataforma ainda não indica, sincroniza o flag EmailVerified=true.
     * Efeito: o dashboard de administração só mostra para ativação contas cujo
     * email já foi verificado (o flag fica marcado no primeiro login da pessoa
     * após verificar o email). Best-effort: uma falha não bloqueia a sessão.
     */
    async function markEmailVerifiedIfChanged(currentUser, record, path) {
        if (!currentUser || currentUser.emailVerified !== true) return;
        if (record.EmailVerified === true) return;
        try {
            const updates = {};
            updates[`/${path}/${currentUser.uid}/EmailVerified`] = true;
            await update(ref(db), updates);
        } catch (err) {
            console.error("Falha ao atualizar o flag EmailVerified:", err);
        }
    }

    // Descobre o papel do utilizador logo após (de)sautenticar
    const resolveRole = useCallback(async (currentUser) => {
        setRoleLoading(true);
        try {
            if (!currentUser) {
                setRole(null);
                setProfile(null);
                return;
            }

            const email = (currentUser.email || "").toLowerCase();

            if (ADMIN_EMAILS.some(a => a.toLowerCase() === email)) {
                setRole("admin");
                setProfile(null);
                return;
            }

            const association = await getAssociationByEmail(email);
            if (association) {
                setRole("association");
                setProfile(association);
                markEmailVerifiedIfChanged(currentUser, association, "Association");
                return;
            }

            const engineer = await getEngineerByEmail(email);
            if (engineer) {
                setRole("engineer");
                setProfile(engineer);
                markEmailVerifiedIfChanged(currentUser, engineer, "User");
                return;
            }

            setRole("none");
            setProfile(null);
        } finally {
            setRoleLoading(false);
        }
    }, []);

    /**
     * Atualiza o perfil do utilizador atual a partir do RTDB.
     * Útil depois de editar o perfil (o contexto pode ainda ter os dados antigos).
     */
    const refreshProfile = useCallback(async () => {
        if (!auth.currentUser) return;
        await resolveRole(auth.currentUser);
    }, [resolveRole]);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            setAuthLoading(false);
            setUser(currentUser);
            resolveRole(currentUser);
        });
        return () => {
            unsubscribe();
        };
    }, [resolveRole]);

    return (
        <userAuthContext.Provider
            value={{user, role, profile, roleLoading, authLoading, logIn, logOut, signUp, verifyEmail, refreshProfile}}>{children}</userAuthContext.Provider>)

}

export function useUserAuth() {
    return useContext(userAuthContext);
}